/** @jsxImportSource @emotion/react */
import React, { useEffect, useImperativeHandle, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress } from '@mui/material';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import {
  getMdInvestResultTextSummary,
  saveMdInvestLineReflectionAmt,
  saveMdInvestResultTextSummary,
} from 'apis/md/MdInvestResult';
import { MdIvestMdlTextSummary } from 'models/md/MdInvestResult';
import CustomTextarea from 'components/inputs/CustomTextarea';
import { useMessageBar } from '../../../../../hooks/useMessageBar';

/**
 * @desc 투자비 산출 등록/조회 > 투자비 산출 등록 > 투자비 산출 결과 분석 탭 > 형개발 투자비 검토 결과
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.14   jdseong       신규 생성
 */
interface Props {
  elmPrjId?: string;
  ivesMdlId?: string;
  isReadOnly?: boolean;
}

const textareaStyle: React.CSSProperties = {
  height: 182,
  fontSize: '0.925rem',
};

const InvestmentReviewSummary = (
  { elmPrjId, ivesMdlId, isReadOnly = false, ...props }: Props,
  ref
) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const [textSummaryData, setTextSummaryData] = useState<MdIvestMdlTextSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    search: (elmPrjId, ivesMdlId) => {
      handleSearch(elmPrjId, ivesMdlId);
    },
    save: (showMsg = true) => {
      return new Promise((resolve) => {
        handleSave(showMsg).then((res) => {
          resolve(res);
        });
      });
    },
  }));

  useEffect(() => {
    handleSearch(elmPrjId, ivesMdlId);
  }, [elmPrjId, ivesMdlId]);

  const validate = () => {
    return true;
  };

  const handleSave = useCallback(
    (showMsg = true) => {
      return new Promise((resolve) => {
        if (validate()) {
          saveMdInvestResultTextSummary({
            elmPrjId: elmPrjId,
            ivesMdlId: ivesMdlId,
            ivesRflcAtcCtn: textSummaryData?.ivesRflcAtcCtn ?? '',
          }).then((res) => {
            if (showMsg) {
              openMessageBar({
                type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
                content:
                  res?.successOrNot === 'Y'
                    ? t('com.label.저장되었습니다.', '저장되었습니다.')
                    : res.data && typeof res.data === 'string'
                    ? res.data
                    : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
              });
            }
            resolve(res);
          });
        } else {
          resolve(null);
        }
      });
    },
    [textSummaryData?.ivesRflcAtcCtn]
  );

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextSummaryData((prev) => ({ ...prev, ivesRflcAtcCtn: e.target.value }));
  };

  const handleSearch = (elmPrjId, ivesMdlId) => {
    if (!elmPrjId || !ivesMdlId) {
      setTextSummaryData(null);
      return;
    }

    setLoading(true);
    getMdInvestResultTextSummary(elmPrjId, ivesMdlId)
      .then((result) => {
        setTextSummaryData(result);
      })
      .catch((error) => {
        setTextSummaryData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderTextContent = () => {
    if (!textSummaryData) {
      return (
        <Typography color="textSecondary">
          {t('md.message.데이터가 없습니다', '데이터가 없습니다.')}
        </Typography>
      );
    }

    const {
      bildPlntCdNm,
      eltrLnNm,
      cellLnNm,
      simMdlNm,
      eltrTotalAmt,
      cellTotalAmt,
      etcTotalAmt,
      totalAmt,
      eltrAmtPerLn,
      cellAmtPerLn,
      top1Item,
      ivesRflcAtcCtn,
    } = textSummaryData;

    // 백엔드에서 생성된 텍스트가 있으면 사용
    return (
      <CustomTextarea
        value={ivesRflcAtcCtn}
        /*wrapStyle={wrapStyle}*/
        textareaStyle={textareaStyle}
        onChange={handleOnChange}
        readOnly={isReadOnly}
      />
    );
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.형개발 투자비 검토 결과', '형개발 투자비 검토 결과')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      {renderTextContent()}
    </>
  );
};

export default React.forwardRef(InvestmentReviewSummary);
